import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Heart, Award } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const ICONS = [ShieldCheck, Heart, Award];

const About = () => {
  const { t } = useLanguage();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-8 md:py-12 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 100% 50%, rgba(31,107,122,0.07) 0%, transparent 65%)",
        }}
      />
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-12 md:grid-cols-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            <span className="eyebrow" data-testid="about-eyebrow">
              {t.about.eyebrow}
            </span>
            <h2 className="display display-2 mt-4 text-[color:var(--kompass-ink)]">
              {t.about.title}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-7 text-base md:text-lg leading-relaxed text-[color:var(--kompass-ink-soft)] md:pt-3"
            data-testid="about-lead"
          >
            {t.about.lead}
          </motion.p>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {t.about.pillars.map((p, i) => {
            const Icon = ICONS[i] || ShieldCheck;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="rounded-2xl border border-[rgba(31,107,122,0.1)] bg-white/60 p-7 hover:bg-white transition-colors"
                data-testid={`about-pillar-${i}`}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl"
                  style={{ background: "rgba(31,107,122,0.08)" }}
                >
                  <Icon
                    className="h-6 w-6"
                    strokeWidth={1.5}
                    style={{ color: "var(--kompass-primary)" }}
                  />
                </div>
                <h3 className="display mt-5 text-xl font-semibold text-[color:var(--kompass-ink)]">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--kompass-ink-soft)]">
                  {p.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
